/**
 * Sessions / "My devices" — admin self-service device management.
 *
 * Lists every active refresh-token session for the calling user and lets
 * them revoke any (except the current one) or "Log out from other devices"
 * in a single click. Useful when an admin's phone is lost or when they
 * share a workstation.
 *
 * "Current session" is detected on the server via a last_used_at heuristic
 * (within 5 min of the request). Revoke buttons are disabled for the row
 * marked is_current = true.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Clock,
  Globe,
  Laptop,
  Smartphone,
  ShieldOff,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';

interface SessionItem {
  id: string;
  device_label: string | null;
  ip: string | null;
  user_agent: string | null;
  app_id: string | null;
  issued_at: string;
  expires_at: string;
  last_used_at: string;
  revoked_at: string | null;
  revoke_reason: string | null;
  is_current: boolean;
}

interface SessionsResponse {
  items: SessionItem[];
  current_session_id: string | null;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return iso;
  }
}

function deviceIcon(appId: string | null, userAgent: string | null): JSX.Element {
  const id = (appId ?? '').toLowerCase();
  const ua = (userAgent ?? '').toLowerCase();
  if (id.includes('mobile') || ua.includes('android') || ua.includes('iphone') || ua.includes('expo')) {
    return <Smartphone className="h-5 w-5 text-vt-primary" />;
  }
  return <Laptop className="h-5 w-5 text-vt-primary" />;
}

function appLabel(appId: string | null): string {
  switch (appId) {
    case 'web-admin':
      return 'HoodDesk Admin (web)';
    case 'resident-mobile':
      return 'Resident app';
    case 'guard-mobile':
      return 'Guard app';
    case 'super-admin':
      return 'Super-admin console';
    case null:
    case undefined:
      return 'Unknown client';
    default:
      return appId;
  }
}

export function SessionsPage(): JSX.Element {
  const qc = useQueryClient();
  const [confirmRevokeOthers, setConfirmRevokeOthers] = useState(false);

  const query = useQuery({
    queryKey: ['me', 'sessions'],
    queryFn: () => get<SessionsResponse>('/me/sessions')
  });

  const revoke = useMutation({
    mutationFn: (sessionId: string) => post(`/me/sessions/${sessionId}/revoke`),
    onSuccess: () => {
      toast.success('Session revoked');
      void qc.invalidateQueries({ queryKey: ['me', 'sessions'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const revokeOthers = useMutation({
    mutationFn: () => post<{ revoked_count: number }>('/me/sessions/revoke-others'),
    onSuccess: (res) => {
      toast.success(
        res.revoked_count === 0
          ? 'No other sessions to revoke'
          : `Revoked ${res.revoked_count} other session${res.revoked_count === 1 ? '' : 's'}`
      );
      void qc.invalidateQueries({ queryKey: ['me', 'sessions'] });
      setConfirmRevokeOthers(false);
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
      setConfirmRevokeOthers(false);
    }
  });

  const items = query.data?.items ?? [];
  const others = useMemo(() => items.filter((s) => !s.is_current && !s.revoked_at), [items]);

  return (
    <>
      <PageHeader
        title="Sessions"
        description="Devices currently signed in to your HoodDesk account."
        actions={
          <Button
            size="sm"
            variant="secondary"
            disabled={others.length === 0}
            onClick={() => setConfirmRevokeOthers(true)}
          >
            <ShieldOff className="h-4 w-4" /> Log out from other devices
          </Button>
        }
      />
      <div className="mx-auto w-full max-w-3xl space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {query.isLoading ? (
          <>
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </>
        ) : query.isError ? (
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4 text-vt-text-muted">
              <AlertTriangle className="h-5 w-5 text-vt-alert" />
              <span>Could not load sessions.</span>
              <Button size="sm" variant="secondary" onClick={() => void query.refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Clock className="h-6 w-6" strokeWidth={1.75} />}
            title="No active sessions"
            description="When you sign in from a device, it will show up here."
          />
        ) : (
          items.map((s) => (
            <Card key={s.id}>
              <CardContent className="flex items-start justify-between gap-vt-3 p-vt-4">
                <div className="flex items-start gap-vt-3">
                  <div className="mt-vt-1">{deviceIcon(s.app_id, s.user_agent)}</div>
                  <div className="flex flex-col gap-vt-1">
                    <div className="flex items-center gap-vt-2">
                      <span className="font-medium text-vt-text">
                        {s.device_label ?? appLabel(s.app_id)}
                      </span>
                      {s.is_current ? (
                        <Badge variant="ok" className="uppercase">
                          This device
                        </Badge>
                      ) : null}
                      {s.revoked_at ? (
                        <Badge variant="muted" className="uppercase">
                          Revoked
                        </Badge>
                      ) : null}
                    </div>
                    <div className="text-vt-12 text-vt-text-muted">
                      <div className="flex items-center gap-vt-2">
                        <Globe className="h-3.5 w-3.5" />
                        <span>{s.ip ?? 'IP unknown'}</span>
                        <span>·</span>
                        <span title={s.user_agent ?? ''}>{appLabel(s.app_id)}</span>
                      </div>
                      <div className="flex items-center gap-vt-2 mt-vt-1">
                        <Clock className="h-3.5 w-3.5" />
                        <span>Last used {formatDateTime(s.last_used_at)}</span>
                        <span>·</span>
                        <span>Expires {formatDateTime(s.expires_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="shrink-0">
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={s.is_current || Boolean(s.revoked_at) || revoke.isPending}
                    onClick={() => revoke.mutate(s.id)}
                  >
                    Revoke
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={confirmRevokeOthers} onOpenChange={setConfirmRevokeOthers}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log out from other devices?</DialogTitle>
            <DialogDescription>
              This will sign out every other device you're signed in on. Your current device will
              remain signed in.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmRevokeOthers(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={revokeOthers.isPending}
              loadingText="Revoking…"
              onClick={() => revokeOthers.mutate()}
            >
              Log out other devices
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
